import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getImageUrl } from '@/utils/imageUtils';

const AutoScrollGallery = ({ images, onImageClick }) => {
  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleImages, setVisibleImages] = useState([]);
  const animationRef = useRef(null);
  const translateXRef = useRef(0);

  // Standard thumbnail size
  const THUMBNAIL_WIDTH = 192; // h-48 = 192px
  const GAP = 16; // gap-4 = 16px
  const ITEM_WIDTH = THUMBNAIL_WIDTH + GAP;
  const SCROLL_SPEED = 0.5; // pixels per frame (slower = smoother)

  useEffect(() => {
    if (images.length === 0) return;

    const calculateVisibleCount = () => {
      const containerWidth = window.innerWidth - 80; // Account for padding
      const count = Math.ceil(containerWidth / ITEM_WIDTH) + 2; // +2 for smooth transition
      return count;
    };

    const visibleCount = calculateVisibleCount();
    
    // Create an array with enough images to fill the screen + buffer
    // Repeat the images array to create seamless loop
    const repeatedImages = [];
    const repeatCount = Math.ceil(visibleCount / images.length) + 2;
    for (let i = 0; i < repeatCount; i++) {
      repeatedImages.push(...images);
    }
    
    setVisibleImages(repeatedImages);

    // Handle window resize
    const handleResize = () => {
      const newVisibleCount = calculateVisibleCount();
      const newRepeatedImages = [];
      const newRepeatCount = Math.ceil(newVisibleCount / images.length) + 2;
      for (let i = 0; i < newRepeatCount; i++) {
        newRepeatedImages.push(...images);
      }
      setVisibleImages(newRepeatedImages);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [images]);

  useEffect(() => {
    if (visibleImages.length === 0 || isPaused) return;

    const animate = () => {
      translateXRef.current -= SCROLL_SPEED;

      // Reset position when one full set of original images has scrolled past
      const resetPoint = -(images.length * ITEM_WIDTH);
      if (translateXRef.current <= resetPoint) {
        translateXRef.current = 0;
      }

      if (containerRef.current) {
        containerRef.current.style.transform = `translateX(${translateXRef.current}px)`;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [visibleImages, isPaused, images.length]);

  // Manual scroll functions
  const handleManualScroll = (direction) => {
    const scrollAmount = ITEM_WIDTH * 3; // Scroll 3 images at a time
    
    if (direction === 'left') {
      translateXRef.current += scrollAmount;
      if (translateXRef.current > 0) {
        translateXRef.current = -(images.length * ITEM_WIDTH) + scrollAmount;
      }
    } else {
      translateXRef.current -= scrollAmount;
      const resetPoint = -(images.length * ITEM_WIDTH);
      if (translateXRef.current <= resetPoint) {
        translateXRef.current = 0;
      }
    }
    
    if (containerRef.current) {
      containerRef.current.style.transform = `translateX(${translateXRef.current}px)`;
    }
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h3 className="text-2xl font-bold text-white mb-4 text-center">
        🌊 Community Gallery
      </h3>
      <div className="relative">
        {/* Left Arrow */}
        <button
          onClick={() => handleManualScroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all hover:scale-110 ml-2"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => handleManualScroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all hover:scale-110 mr-2"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>

        <div
          className="overflow-hidden bg-black/30 rounded-lg p-4 backdrop-blur-sm"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
        <div
          ref={containerRef}
          className="flex gap-4"
          style={{
            willChange: 'transform',
            transition: isPaused ? 'transform 0.3s ease-out' : 'none'
          }}
        >
          {visibleImages.map((image, index) => (
            <div
              key={`${image.image_url}-${index}`}
              className="relative group cursor-pointer flex-shrink-0"
              style={{ width: `${THUMBNAIL_WIDTH}px` }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onImageClick(index % images.length);
              }}
            >
              <img
                src={getImageUrl(image.image_url)}
                alt={`Photo by ${image.username}`}
                className="h-48 w-full rounded-lg object-cover transition-transform duration-300 group-hover:scale-105 group-hover:shadow-2xl"
                loading="lazy"
                draggable="false"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex flex-col justify-end p-3 pointer-events-none">
                <p className="text-white text-sm font-semibold truncate">
                  📸 {image.username}
                </p>
                <p className="text-cyan-300 text-xs truncate">
                  {image.dive_center}
                </p>
              </div>
            </div>
          ))}
        </div>
        </div>
      </div>
      <p className="text-gray-400 text-sm text-center mt-2">
        Hover to pause • Click to view full size • Use arrows to navigate
      </p>
    </div>
  );
};

export default AutoScrollGallery;
